import { useEffect, useState } from 'react';
import { defaultCountries, parseCountry } from 'react-international-phone';
import { fetchCountries } from 'services/api/country';
import { CountryOption } from 'types/country';

const isValidIso2 = (value: string | undefined | null): value is string =>
    !!value && /^[a-zA-Z]{2}$/.test(value);

let cachedOptions: CountryOption[] | null = null;
let inflight: Promise<CountryOption[]> | null = null;

const toCountryOptions = (codes: Map<string, string>): CountryOption[] =>
    defaultCountries
        .map(parseCountry)
        .filter((country) => (codes.size ? codes.has(country.iso2.toUpperCase()) : true))
        .map((country) => ({
            value: country.iso2.toUpperCase(),
            label: codes.get(country.iso2.toUpperCase()) || country.name,
            iso2: country.iso2,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

const loadCountryOptions = async (): Promise<CountryOption[]> => {
    if (cachedOptions) return cachedOptions;
    if (inflight) return inflight;

    inflight = fetchCountries()
        .then((items) => {
            const byCode = new Map(
                items
                    .map((item) => [item.code?.toUpperCase(), item.name] as const)
                    .filter(([code]) => isValidIso2(code)) as [string, string][]
            );
            const options = toCountryOptions(byCode);
            cachedOptions = options.length ? options : toCountryOptions(new Map());
            return cachedOptions;
        })
        .catch((error) => {
            console.warn('Failed to load countries', error);
            cachedOptions = toCountryOptions(new Map());
            return cachedOptions;
        })
        .finally(() => {
            inflight = null;
        });

    return inflight;
};

export const useCountries = () => {
    const [countries, setCountries] = useState<CountryOption[]>(cachedOptions ?? []);
    const [loading, setLoading] = useState(!cachedOptions);

    useEffect(() => {
        let cancelled = false;

        loadCountryOptions()
            .then((options) => {
                if (!cancelled) setCountries(options);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    return { countries, loading };
};
